import axios from 'axios';

// Empty REACT_APP_BACKEND_URL → same-origin /api (production behind Caddy).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${BACKEND_URL}/api`;

const TOKEN_KEY = 'bot_admin_token';

export function getAdminToken() {
  return localStorage.getItem(TOKEN_KEY) || '';
}

export function setAdminToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = getAdminToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    if (status === 401 && !String(url).includes('/auth/login') && window.location.pathname !== '/login') {
      setAdminToken('');
      window.location.assign('/login');
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  login: (username, password) => api.post('/auth/login', { username, password }),
};

// Channels
export const channelsApi = {
  getAll: () => api.get('/channels'),
  getById: (id) => api.get(`/channels/${id}`),
  create: (data) => api.post('/channels', data),
  update: (id, data) => api.put(`/channels/${id}`, data),
  delete: (id) => api.delete(`/channels/${id}`),
};

// Tariffs
export const tariffsApi = {
  getAll: (channelId) => api.get('/tariffs', { params: { channel_id: channelId } }),
  getById: (id) => api.get(`/tariffs/${id}`),
  create: (data) => api.post('/tariffs', data),
  update: (id, data) => api.put(`/tariffs/${id}`, data),
  delete: (id) => api.delete(`/tariffs/${id}`),
};

// Packages
export const packagesApi = {
  getAll: () => api.get('/packages'),
  getById: (id) => api.get(`/packages/${id}`),
  create: (data) => api.post('/packages', data),
  update: (id, data) => api.put(`/packages/${id}`, data),
  delete: (id) => api.delete(`/packages/${id}`),
};

// Subscriptions
export const subscriptionsApi = {
  getAll: (params) => api.get('/subscriptions', { params }),
  getById: (id) => api.get(`/subscriptions/${id}`),
  create: (data) => api.post('/subscriptions', data),
  update: (id, data) => api.put(`/subscriptions/${id}`, data),
  activate: (id) => api.post(`/subscriptions/${id}/activate`),
  grant: (data) => api.post('/subscriptions/grant', data),
  regenerateInvite: (id) => api.post(`/subscriptions/${id}/regenerate-invite?notify_user=true`),
  delete: (id) => api.delete(`/subscriptions/${id}`),
  checkExpired: () => api.post('/subscriptions/check-expired'),
  runExpirationCheck: () => api.post('/subscriptions/run-expiration-check'),
};

// Broadcasts
export const broadcastsApi = {
  getAll: () => api.get('/broadcasts'),
  create: (data) => api.post('/broadcasts', data),
  update: (id, data) => api.put(`/broadcasts/${id}`, data),
  delete: (id) => api.delete(`/broadcasts/${id}`),
  preview: (data) => api.post('/broadcasts/preview', data),
  send: (id) => api.post(`/broadcasts/${id}/send`),
  cancel: (id) => api.post(`/broadcasts/${id}/cancel`),
  recipients: (id, params) => api.get(`/broadcasts/${id}/recipients`, { params }),
};

// Referrals
export const referralsApi = {
  leaderboard: (limit = 100) => api.get('/referrals/leaderboard', { params: { limit } }),
};

// Channel audit (ghosts)
export const channelAuditApi = {
  audit: (channelId) => api.post(`/channels/${channelId}/audit`, null, { timeout: 90000 }),
  kick: (channelId, data) => api.post(`/channels/${channelId}/audit/kick`, data, { timeout: 30000 }),
  kickAllExpired: (channelId) =>
    api.post(`/channels/${channelId}/audit/kick-all-expired`, null, { timeout: 120000 }),
};

// Users
export const usersApi = {
  resolve: (ref) => api.post('/users/resolve', { ref }),
};

// Payments
export const paymentsApi = {
  getAll: (params) => api.get('/payments', { params }),
  getById: (id) => api.get(`/payments/${id}`),
  create: (data) => api.post('/payments', data),
  confirm: (id) => api.post(`/payments/${id}/confirm`),
  cancel: (id) => api.post(`/payments/${id}/cancel`),
};

// Payment Methods
export const paymentMethodsApi = {
  getAll: () => api.get('/payment-methods'),
  getById: (id) => api.get(`/payment-methods/${id}`),
  create: (data) => api.post('/payment-methods', data),
  update: (id, data) => api.put(`/payment-methods/${id}`, data),
  delete: (id) => api.delete(`/payment-methods/${id}`),
};

// Promo Codes
export const promoCodesApi = {
  getAll: () => api.get('/promo-codes'),
  getById: (id) => api.get(`/promo-codes/${id}`),
  create: (data) => api.post('/promo-codes', data),
  update: (id, data) => api.put(`/promo-codes/${id}`, data),
  delete: (id) => api.delete(`/promo-codes/${id}`),
  validate: (code, channelId, tariffId) => api.post('/promo-codes/validate', null, {
    params: { code, channel_id: channelId, tariff_id: tariffId }
  }),
};

// Settings
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  setupWebhook: (webhookUrl) => api.post('/settings/setup-webhook', null, { params: { webhook_url: webhookUrl } }),
  getWebhookInfo: () => api.get('/settings/webhook-info'),
  getStarsWallet: () => api.get('/settings/stars-wallet'),
  updateStarsWallet: (data) => api.put('/settings/stars-wallet', data),
  checkStarsWallet: (token) => api.post('/settings/stars-wallet/check', { token: token || null }),
};

// Admins
export const adminsApi = {
  getAll: () => api.get('/admins'),
  getById: (id) => api.get(`/admins/${id}`),
  create: (data) => api.post('/admins', data),
  update: (id, data) => api.put(`/admins/${id}`, data),
  delete: (id) => api.delete(`/admins/${id}`),
};

// Stats
export const statsApi = {
  get: () => api.get('/stats'),
};

// Export
export const exportApi = {
  subscriptions: (format = 'json') => api.get(`/export/subscriptions?format=${format}`),
  payments: (format = 'json') => api.get(`/export/payments?format=${format}`),
};

export default api;
